import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import Konva from "konva";
import { EditorTool, EditorObject, CanvasSize, DisplaySize, generateId } from "../types/editorTypes";

/**
 * テキスト内のクリック位置から文字インデックスを計算する
 */
const getCharIndexAtPoint = (
  text: string,
  localX: number,
  localY: number,
  fontSize: number,
  fontFamily: string,
  fontStyle: string,
  textWidth: number,
  textAlign: string = 'left',
  verticalAlign: string = 'top',
  containerHeight: number = 0,
  padding: number = 0,
): number => {
  if (!text) return 0;

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return text.length;

  // Konvaと同じフォント設定を再現
  const fontWeight = fontStyle === 'bold' ? 'bold ' : fontStyle === 'italic' ? 'italic ' : '';
  ctx.font = `${fontWeight}${fontSize}px ${fontFamily}`;

  // Konvaデフォルトの行高さ
  const lineHeight = fontSize;

  // テキストを視覚的な行に分割（\nとchar wrappingの両方を考慮）
  const visualLines: { text: string; startIndex: number }[] = [];
  const paragraphs = text.split('\n');
  let globalIndex = 0;

  for (let p = 0; p < paragraphs.length; p++) {
    const para = paragraphs[p];
    if (para.length === 0) {
      visualLines.push({ text: '', startIndex: globalIndex });
    } else {
      let lineStart = 0;
      while (lineStart < para.length) {
        let lineEnd = lineStart + 1;
        while (lineEnd <= para.length) {
          const lineText = para.slice(lineStart, lineEnd);
          if (ctx.measureText(lineText).width > textWidth && lineEnd - lineStart > 1) {
            lineEnd--;
            break;
          }
          lineEnd++;
        }
        lineEnd = Math.min(lineEnd, para.length);
        visualLines.push({
          text: para.slice(lineStart, lineEnd),
          startIndex: globalIndex + lineStart,
        });
        lineStart = lineEnd;
      }
    }
    globalIndex += para.length + 1; // +1 for \n
  }

  if (visualLines.length === 0) return 0;

  // verticalAlignのオフセットを計算してlocalYから引く
  let vAlignOffset = 0;
  if (verticalAlign !== 'top' && containerHeight > 0) {
    const textHeight = visualLines.length * lineHeight;
    const availableHeight = containerHeight - padding * 2;
    const freeSpace = availableHeight - textHeight;
    if (freeSpace > 0) {
      if (verticalAlign === 'middle') vAlignOffset = freeSpace / 2;
      else if (verticalAlign === 'bottom') vAlignOffset = freeSpace;
    }
  }
  const adjustedY = localY - vAlignOffset;

  // クリックされた行を特定
  const clickedLineIdx = Math.max(0, Math.min(
    Math.floor(Math.max(0, adjustedY) / lineHeight),
    visualLines.length - 1
  ));

  const line = visualLines[clickedLineIdx];

  // テキスト配置によるオフセットを計算
  const lineTextWidth = ctx.measureText(line.text).width;
  let alignOffset = 0;
  if (textAlign === 'center') {
    alignOffset = (textWidth - lineTextWidth) / 2;
  } else if (textAlign === 'right') {
    alignOffset = textWidth - lineTextWidth;
  }

  const adjustedX = localX - alignOffset;
  if (adjustedX <= 0) return line.startIndex;

  // 行内の文字位置を特定
  for (let i = 0; i <= line.text.length; i++) {
    const w = ctx.measureText(line.text.slice(0, i)).width;
    if (w >= adjustedX) {
      const prevW = i > 0 ? ctx.measureText(line.text.slice(0, i - 1)).width : 0;
      const bestPos = (adjustedX - prevW < w - adjustedX) ? Math.max(0, i - 1) : i;
      return line.startIndex + bestPos;
    }
  }

  return line.startIndex + line.text.length;
};

interface UseCanvasDrawingProps {
  stageRef: React.RefObject<Konva.Stage>;
  containerRef: React.RefObject<HTMLDivElement>;
  transformerRef: React.RefObject<Konva.Transformer>;
  sourceImg: HTMLImageElement | null;
  // ツール設定
  currentTool: EditorTool;
  setCurrentTool: (tool: EditorTool) => void;
  // マーカー設定
  markerColor: string;
  markerStrokeWidth: number;
  // 四角形設定
  rectFillEnabled: boolean;
  rectFillColor: string;
  rectStrokeEnabled: boolean;
  rectStrokeColor: string;
  // テキスト設定
  textFontSize: number;
  textFontFamily: string;
  textFontStyle: 'normal' | 'bold' | 'italic';
  textAlign: 'left' | 'center' | 'right' | 'justify';
  verticalAlign: 'top' | 'middle' | 'bottom';
  textColor: string;
  textBgColor: string;
  // 帯削除コールバック
  onBandDelete?: () => void;
}

interface UseCanvasDrawingReturn {
  // State
  objects: EditorObject[];
  setObjects: React.Dispatch<React.SetStateAction<EditorObject[]>>;
  selectedId: string | null;
  setSelectedId: (id: string | null) => void;
  isDrawing: boolean;
  currentPoints: number[];
  canvasSize: CanvasSize;
  displaySize: DisplaySize;
  cursorVisible: boolean;
  loadedImages: { [key: string]: HTMLImageElement };
  // テキスト編集
  editingTextId: string | null;
  setEditingTextId: (id: string | null) => void;
  editingTextValue: string;
  setEditingTextValue: (value: string) => void;
  editingTextPosition: { x: number; y: number; width: number; height: number } | null;
  setEditingTextPosition: (pos: { x: number; y: number; width: number; height: number } | null) => void;
  cursorPosition: number;
  setCursorPosition: (pos: number) => void;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  // Handlers
  handleMouseDown: (e: Konva.KonvaEventObject<MouseEvent>) => void;
  handleMouseMove: () => void;
  handleMouseUp: () => void;
  handleStageClick: (e: Konva.KonvaEventObject<MouseEvent>) => void;
  handleObjectSelect: (id: string) => void;
  handleDragEnd: (id: string, e: Konva.KonvaEventObject<DragEvent>) => void;
  handleDeleteSelected: () => void;
  handleTextDoubleClick: (obj: EditorObject, e?: Konva.KonvaEventObject<any>) => void;
  handleRectDoubleClick: (obj: EditorObject, e?: Konva.KonvaEventObject<any>) => void;
  handleEditingClick: (obj: EditorObject, e: Konva.KonvaEventObject<any>) => void;
  handleTextEditConfirm: (newHeight?: number) => void;
  updateTextProperty: (property: keyof EditorObject, value: any) => void;
  updateRectProperty: (property: keyof EditorObject, value: any) => void;
  handleAddImage: (event: React.ChangeEvent<HTMLInputElement>) => void;
  refreshTransformer: () => void;
  // コンテキストメニュー（階層変更）
  contextMenu: { x: number; y: number; targetId: string } | null;
  handleObjectContextMenu: (id: string, e: Konva.KonvaEventObject<PointerEvent>) => void;
  closeContextMenu: () => void;
  bringToFront: () => void;
  bringForward: () => void;
  sendBackward: () => void;
  sendToBack: () => void;
}

export const useCanvasDrawing = ({
  stageRef,
  containerRef,
  transformerRef,
  sourceImg,
  currentTool,
  setCurrentTool,
  markerColor,
  markerStrokeWidth,
  rectFillEnabled,
  rectFillColor,
  rectStrokeEnabled,
  rectStrokeColor,
  textFontSize,
  textFontFamily,
  textFontStyle,
  textAlign,
  verticalAlign,
  textColor,
  textBgColor,
  onBandDelete,
}: UseCanvasDrawingProps): UseCanvasDrawingReturn => {
  // State
  const [objects, setObjects] = useState<EditorObject[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPoints, setCurrentPoints] = useState<number[]>([]);
  const [canvasSize, setCanvasSize] = useState<CanvasSize>({ width: 800, height: 600 });
  const [cursorVisible, setCursorVisible] = useState(true);
  const [loadedImages, setLoadedImages] = useState<{ [key: string]: HTMLImageElement }>({});

  // テキスト編集
  const [editingTextId, setEditingTextId] = useState<string | null>(null);
  const [editingTextValue, setEditingTextValue] = useState('');
  const [editingTextPosition, setEditingTextPosition] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const [cursorPosition, setCursorPosition] = useState<number>(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // コンテナサイズを監視（ResizeObserverを使用）
  useEffect(() => {
    if (!containerRef.current) return;

    const updateSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          const width = Math.max(rect.width, 400);
          const height = Math.max(rect.height, 300);
          setCanvasSize(prev => {
            if (prev.width === width && prev.height === height) {
              return prev;
            }
            return { width, height };
          });
        }
      }
    };

    const resizeObserver = new ResizeObserver(() => {
      updateSize();
    });
    resizeObserver.observe(containerRef.current);
    updateSize();

    return () => {
      resizeObserver.disconnect();
    };
  }, [sourceImg, containerRef]);

  // Transformer更新
  useEffect(() => {
    if (!transformerRef.current || !stageRef.current) return;

    const stage = stageRef.current;
    const transformer = transformerRef.current;

    // 一度Transformerを外す
    transformer.nodes([]);

    // 次のフレームで再アタッチ（Reactの再レンダリング完了後）
    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!stageRef.current || !transformerRef.current) return;
        const selectedNode = stageRef.current.findOne(`#${selectedId}`);
        if (selectedNode) {
          transformerRef.current.nodes([selectedNode]);
        }
        transformerRef.current.getLayer()?.batchDraw();
      });
    });

    return () => cancelAnimationFrame(rafId);
  }, [selectedId, editingTextId, transformerRef, stageRef, objects]);

  // コンテキストメニュー（階層変更）
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; targetId: string } | null>(null);

  const handleObjectContextMenu = useCallback((id: string, e: Konva.KonvaEventObject<PointerEvent>) => {
    e.evt.preventDefault();
    e.evt.stopPropagation();
    e.cancelBubble = true;
    setSelectedId(id);
    const stage = stageRef.current;
    if (!stage) return;
    const container = stage.container().getBoundingClientRect();
    const menuWidth = 110;
    const menuHeight = 176; // py-2(8*2) + 4 buttons * (text 20 + py-2*2(16)) = ~176
    let x = e.evt.clientX - container.left;
    let y = e.evt.clientY - container.top;
    // 右側にはみ出す場合は左側に表示
    if (x + menuWidth > container.width) {
      x = container.width - menuWidth;
    }
    // 下側にはみ出す場合は上側に表示
    if (y + menuHeight > container.height) {
      y = container.height - menuHeight;
    }
    // 負の値にならないよう補正
    if (x < 0) x = 0;
    if (y < 0) y = 0;
    setContextMenu({ x, y, targetId: id });
  }, [stageRef]);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  // メニュー外クリックで閉じる
  useEffect(() => {
    if (!contextMenu) return;
    const handleMouseDown = () => setContextMenu(null);
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [contextMenu]);

  const bringToFront = useCallback(() => {
    if (!contextMenu) return;
    setObjects(prev => {
      const idx = prev.findIndex(o => o.id === contextMenu.targetId);
      if (idx === -1 || idx === prev.length - 1) return prev;
      const next = [...prev];
      const [item] = next.splice(idx, 1);
      next.push(item);
      return next;
    });
    setContextMenu(null);
  }, [contextMenu]);

  const bringForward = useCallback(() => {
    if (!contextMenu) return;
    setObjects(prev => {
      const idx = prev.findIndex(o => o.id === contextMenu.targetId);
      if (idx === -1 || idx === prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next;
    });
    setContextMenu(null);
  }, [contextMenu]);

  const sendBackward = useCallback(() => {
    if (!contextMenu) return;
    setObjects(prev => {
      const idx = prev.findIndex(o => o.id === contextMenu.targetId);
      if (idx <= 0) return prev;
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next;
    });
    setContextMenu(null);
  }, [contextMenu]);

  const sendToBack = useCallback(() => {
    if (!contextMenu) return;
    setObjects(prev => {
      const idx = prev.findIndex(o => o.id === contextMenu.targetId);
      if (idx <= 0) return prev;
      const next = [...prev];
      const [item] = next.splice(idx, 1);
      next.unshift(item);
      return next;
    });
    setContextMenu(null);
  }, [contextMenu]);

  // Transformerを手動で更新する関数
  const refreshTransformer = useCallback(() => {
    if (transformerRef.current && stageRef.current && selectedId) {
      const transformer = transformerRef.current;
      const stage = stageRef.current;
      const selectedNode = stage.findOne(`#${selectedId}`);
      if (selectedNode) {
        // 一度ノードを外す
        transformer.nodes([]);
        // すぐに再アタッチしてforceUpdateを呼ぶ
        transformer.nodes([selectedNode]);
        // Transformerに強制的にバウンディングボックスを再計算させる
        (transformer as any).forceUpdate();
        transformer.getLayer()?.batchDraw();
      }
    }
  }, [selectedId, transformerRef, stageRef]);

  // キーボードイベント（バックスペース/Deleteで削除）
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (editingTextId) return;
      const activeElement = document.activeElement;
      if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
        return;
      }

      if ((e.key === 'Backspace' || e.key === 'Delete') && selectedId) {
        e.preventDefault();
        if (selectedId === 'band-image') {
          // 帯画像が選択されている場合は帯削除コールバックを呼ぶ
          if (onBandDelete) {
            onBandDelete();
            setSelectedId(null);
          }
          return;
        }
        setObjects(prev => prev.filter(obj => obj.id !== selectedId));
        setSelectedId(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedId, editingTextId, onBandDelete]);

  // カーソル点滅
  useEffect(() => {
    if (editingTextId) {
      const interval = setInterval(() => {
        setCursorVisible(prev => !prev);
      }, 530);
      return () => clearInterval(interval);
    } else {
      setCursorVisible(true);
    }
  }, [editingTextId]);

  // 画像表示サイズを計算
  const displaySize = useMemo(() => {
    if (sourceImg && canvasSize.width > 0 && canvasSize.height > 0) {
      const padding = 20; // キャンバス内のパディング
      const availableWidth = canvasSize.width - padding * 2;
      const availableHeight = canvasSize.height - padding * 2;

      const imgAspect = sourceImg.width / sourceImg.height;
      const canvasAspect = availableWidth / availableHeight;

      let width, height;
      if (imgAspect > canvasAspect) {
        // 画像が横長 - 幅に合わせる
        width = availableWidth;
        height = availableWidth / imgAspect;
      } else {
        // 画像が縦長 - 高さに合わせる
        height = availableHeight;
        width = availableHeight * imgAspect;
      }

      const offsetX = (canvasSize.width - width) / 2;
      const offsetY = (canvasSize.height - height) / 2;
      return { width, height, offsetX, offsetY };
    }
    return { width: 0, height: 0, offsetX: 0, offsetY: 0 };
  }, [sourceImg, canvasSize]);

  // テキストの書式を更新
  const updateTextProperty = useCallback((property: keyof EditorObject, value: any) => {
    if (!selectedId) return;
    setObjects(prev => prev.map(obj => {
      if (obj.id === selectedId && obj.type === 'text') {
        return { ...obj, [property]: value };
      }
      return obj;
    }));
  }, [selectedId]);

  // 四角形のプロパティを更新
  const updateRectProperty = useCallback((property: keyof EditorObject, value: any) => {
    if (!selectedId) return;
    setObjects(prev => prev.map(obj => {
      if (obj.id === selectedId && obj.type === 'rect') {
        return { ...obj, [property]: value };
      }
      return obj;
    }));
  }, [selectedId]);

  // テキスト編集確定
  const handleTextEditConfirm = useCallback((newHeight?: number) => {
    if (editingTextId) {
      const editingObj = objects.find(obj => obj.id === editingTextId);
      if (editingTextValue.trim()) {
        // テキストがある場合は更新（高さも更新）
        setObjects(prev => prev.map(obj => {
          if (obj.id === editingTextId) {
            return {
              ...obj,
              text: editingTextValue,
              // 新しい高さが元の高さより大きい場合のみ更新
              height: newHeight && newHeight > (obj.height || 0) ? newHeight : obj.height,
            };
          }
          return obj;
        }));
      } else if (editingObj?.type === 'text') {
        // テキストオブジェクトで空の場合のみ削除
        setObjects(prev => prev.filter(obj => obj.id !== editingTextId));
      } else {
        // 四角形の場合はテキストを空に更新
        setObjects(prev => prev.map(obj => {
          if (obj.id === editingTextId) {
            return { ...obj, text: '' };
          }
          return obj;
        }));
      }
    }
    setEditingTextId(null);
    setEditingTextValue('');
    setEditingTextPosition(null);
    // テキスト追加後はツール選択をOFFにする
    setCurrentTool('select');
  }, [editingTextId, editingTextValue, objects, setCurrentTool]);

  // ステージクリック処理
  const handleStageClick = useCallback((e: Konva.KonvaEventObject<MouseEvent>) => {
    const clickedOnEmpty = e.target === e.target.getStage() || e.target.name() === 'background';

    // 編集中のテキストがあれば、空白エリアクリック時に確定＋選択解除
    if (editingTextId) {
      if (clickedOnEmpty) {
        handleTextEditConfirm();
        setSelectedId(null);
      }
      return;
    }

    if (clickedOnEmpty) {
      // テキストツールの場合はドラッグで作成するので、クリックでは何もしない
      if (currentTool !== 'text') {
        setSelectedId(null);
      }
    }
  }, [editingTextId, currentTool, handleTextEditConfirm]);

  // テキストボックスをダブルクリックして編集開始
  const handleTextDoubleClick = useCallback((obj: EditorObject, e?: Konva.KonvaEventObject<any>) => {
    // 既に同じオブジェクトを編集中なら何もしない
    if (editingTextId === obj.id) return;
    setSelectedId(obj.id);
    setEditingTextId(obj.id);
    setEditingTextValue(obj.text || '');

    // ダブルクリック位置からカーソル位置を計算（4pxパディングを考慮）
    const textPad = 4;
    const textWidth = (obj.width || 150) - textPad * 2;
    let pos = (obj.text || '').length;
    if (e) {
      const pointerPos = stageRef.current?.getPointerPosition();
      if (pointerPos) {
        const localX = pointerPos.x - obj.x - textPad;
        const localY = pointerPos.y - obj.y - textPad;
        pos = getCharIndexAtPoint(
          obj.text || '',
          localX,
          localY,
          obj.fontSize || 14,
          obj.fontFamily || 'sans-serif',
          obj.fontStyle || 'normal',
          textWidth,
          obj.textAlign || 'left',
          obj.verticalAlign || 'top',
          obj.height || 24,
          textPad,
        );
      }
    }
    setCursorPosition(pos);

    setEditingTextPosition({
      x: obj.x + textPad,
      y: obj.y + textPad,
      width: textWidth,
      height: obj.height || 24,
    });
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(pos, pos);
      }
    }, 10);
  }, [editingTextId, stageRef]);

  // 四角形をダブルクリックしてテキスト編集開始
  const handleRectDoubleClick = useCallback((obj: EditorObject, e?: Konva.KonvaEventObject<any>) => {
    // 既に同じオブジェクトを編集中なら何もしない
    if (editingTextId === obj.id) return;
    setSelectedId(obj.id);
    setEditingTextId(obj.id);
    setEditingTextValue(obj.text || '');

    // ダブルクリック位置からカーソル位置を計算
    let pos = (obj.text || '').length;
    if (e) {
      const pointerPos = stageRef.current?.getPointerPosition();
      if (pointerPos) {
        // Rect内テキストは4pxパディングあり
        const localX = pointerPos.x - obj.x - 4;
        const localY = pointerPos.y - obj.y - 4;
        pos = getCharIndexAtPoint(
          obj.text || '',
          localX,
          localY,
          obj.fontSize || 14,
          obj.fontFamily || 'sans-serif',
          obj.fontStyle || 'normal',
          (obj.width || 100) - 8,
          obj.textAlign || 'left',
          obj.verticalAlign || 'top',
          obj.height || 50,
          4,
        );
      }
    }
    setCursorPosition(pos);

    // 四角形内のテキストは4pxのパディングがあるため、位置とサイズを調整
    setEditingTextPosition({
      x: obj.x + 4,
      y: obj.y + 4,
      width: (obj.width || 100) - 8,
      height: (obj.height || 50) - 8,
    });
    // TextEditOverlayがマウントされた後にフォーカス
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(pos, pos);
        }
      }, 50);
    });
  }, [editingTextId, stageRef]);

  // 編集中にテキストエリア内をクリックしてカーソル移動
  const handleEditingClick = useCallback((obj: EditorObject, e: Konva.KonvaEventObject<any>) => {
    if (editingTextId !== obj.id) return;
    const pointerPos = stageRef.current?.getPointerPosition();
    if (!pointerPos) return;

    const padX = 4;
    const padY = 4;
    const localX = pointerPos.x - obj.x - padX;
    const localY = pointerPos.y - obj.y - padY;
    const textWidth = obj.type === 'rect' ? (obj.width || 100) - 8 : (obj.width || 150) - 8;

    const defaultHeight = obj.type === 'rect' ? 50 : 24;
    const pos = getCharIndexAtPoint(
      editingTextValue,
      localX,
      localY,
      obj.fontSize || 14,
      obj.fontFamily || 'sans-serif',
      obj.fontStyle || 'normal',
      textWidth,
      obj.textAlign || 'left',
      obj.verticalAlign || 'top',
      obj.height || defaultHeight,
      padY,
    );
    setCursorPosition(pos);
    if (textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(pos, pos);
    }
  }, [editingTextId, editingTextValue, stageRef]);

  // 描画開始
  const handleMouseDown = useCallback((e: Konva.KonvaEventObject<MouseEvent>) => {
    if (currentTool === 'marker' || currentTool === 'horizontal' || currentTool === 'rectangle' || currentTool === 'text') {
      const pos = stageRef.current?.getPointerPosition();
      if (pos) {
        setIsDrawing(true);
        setCurrentPoints([pos.x, pos.y]);
      }
    }
  }, [currentTool, stageRef]);

  // 描画中
  const handleMouseMove = useCallback(() => {
    if (!isDrawing) return;
    const pos = stageRef.current?.getPointerPosition();
    if (!pos) return;

    if (currentTool === 'marker') {
      setCurrentPoints(prev => [...prev, pos.x, pos.y]);
    } else if (currentTool === 'horizontal' || currentTool === 'rectangle' || currentTool === 'text') {
      setCurrentPoints(prev => [prev[0], prev[1], pos.x, pos.y]);
    }
  }, [isDrawing, currentTool, stageRef]);

  // 描画終了
  const handleMouseUp = useCallback(() => {
    if (!isDrawing) return;
    setIsDrawing(false);

    if (currentPoints.length < 4) {
      setCurrentPoints([]);
      return;
    }

    if (currentTool === 'marker') {
      const newObj: EditorObject = {
        id: generateId(),
        type: 'marker',
        x: 0,
        y: 0,
        points: currentPoints,
        stroke: markerColor,
        strokeWidth: markerStrokeWidth,
      };
      setObjects(prev => [...prev, newObj]);
    } else if (currentTool === 'horizontal') {
      const newObj: EditorObject = {
        id: generateId(),
        type: 'line',
        x: 0,
        y: 0,
        points: [currentPoints[0], currentPoints[1], currentPoints[2], currentPoints[1]],
        stroke: markerColor,
        strokeWidth: markerStrokeWidth,
      };
      setObjects(prev => [...prev, newObj]);
      setCurrentTool('select');
    } else if (currentTool === 'rectangle') {
      const x = Math.min(currentPoints[0], currentPoints[2]);
      const y = Math.min(currentPoints[1], currentPoints[3]);
      const width = Math.abs(currentPoints[2] - currentPoints[0]);
      const height = Math.abs(currentPoints[3] - currentPoints[1]);
      const newObj: EditorObject = {
        id: generateId(),
        type: 'rect',
        x,
        y,
        width,
        height,
        backgroundColor: rectFillEnabled ? rectFillColor : undefined,
        stroke: rectStrokeEnabled ? rectStrokeColor : undefined,
        strokeWidth: rectStrokeEnabled ? 2 : 0,
      };
      setObjects(prev => [...prev, newObj]);
      setCurrentTool('select');
    } else if (currentTool === 'text') {
      const x = Math.min(currentPoints[0], currentPoints[2]);
      const y = Math.min(currentPoints[1], currentPoints[3]);
      const width = Math.max(50, Math.abs(currentPoints[2] - currentPoints[0]));
      const height = Math.max(20, Math.abs(currentPoints[3] - currentPoints[1]));
      const newId = generateId();
      const newObj: EditorObject = {
        id: newId,
        type: 'text',
        x,
        y,
        text: '',
        fontSize: textFontSize,
        fontFamily: textFontFamily,
        fontStyle: textFontStyle,
        textAlign: textAlign,
        verticalAlign: verticalAlign,
        textColor: textColor,
        backgroundColor: textBgColor,
        width,
        height,
      };
      setObjects(prev => [...prev, newObj]);
      // 作成後すぐに編集モードに入る
      setSelectedId(newId);
      setEditingTextId(newId);
      setEditingTextValue('');
      setCursorPosition(0);
      setEditingTextPosition({ x: x + 4, y: y + 4, width: width - 8, height });
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 10);
    }

    setCurrentPoints([]);
  }, [
    isDrawing, currentPoints, currentTool, setCurrentTool,
    markerColor, markerStrokeWidth,
    rectFillEnabled, rectFillColor, rectStrokeEnabled, rectStrokeColor,
    textFontSize, textFontFamily, textFontStyle, textAlign, verticalAlign, textColor, textBgColor,
  ]);

  // オブジェクト選択
  const handleObjectSelect = useCallback((id: string) => {
    if (currentTool === 'select') {
      setSelectedId(id);
    }
  }, [currentTool]);

  // オブジェクト移動
  const handleDragEnd = useCallback((id: string, e: Konva.KonvaEventObject<DragEvent>) => {
    setObjects(prev => prev.map(obj => {
      if (obj.id === id) {
        return { ...obj, x: e.target.x(), y: e.target.y() };
      }
      return obj;
    }));
  }, []);

  // 選択オブジェクト削除
  const handleDeleteSelected = useCallback(() => {
    if (selectedId) {
      setObjects(prev => prev.filter(obj => obj.id !== selectedId));
      setSelectedId(null);
    }
  }, [selectedId]);

  // 画像を追加
  const handleAddImage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;
    const file = event.target.files[0];

    const reader = new FileReader();
    reader.onload = (e) => {
      const imageUrl = e.target?.result as string;
      if (!imageUrl) return;

      const img = new window.Image();
      img.onload = () => {
        // キャンバスサイズに収まるようにスケール
        const maxWidth = displaySize.width * 0.5;
        const maxHeight = displaySize.height * 0.5;
        let width = img.width;
        let height = img.height;

        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }

        const newId = generateId();
        setLoadedImages(prev => ({ ...prev, [newId]: img }));

        const newObj: EditorObject = {
          id: newId,
          type: 'image',
          x: displaySize.offsetX + (displaySize.width - width) / 2,
          y: displaySize.offsetY + (displaySize.height - height) / 2,
          width,
          height,
          imageUrl,
        };
        setObjects(prev => [...prev, newObj]);
        // ツールを選択モードに切り替え、次のフレームで選択状態にする
        setCurrentTool('select');
        // 次のフレームまで待ってから選択（レンダリング完了後に選択）
        requestAnimationFrame(() => {
          setSelectedId(newId);
        });
      };
      img.src = imageUrl;
    };
    reader.readAsDataURL(file);

    event.target.value = '';
  }, [displaySize, setCurrentTool]);

  return {
    // State
    objects,
    setObjects,
    selectedId,
    setSelectedId,
    isDrawing,
    currentPoints,
    canvasSize,
    displaySize,
    cursorVisible,
    loadedImages,
    // テキスト編集
    editingTextId,
    setEditingTextId,
    editingTextValue,
    setEditingTextValue,
    editingTextPosition,
    setEditingTextPosition,
    cursorPosition,
    setCursorPosition,
    textareaRef,
    // Handlers
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleStageClick,
    handleObjectSelect,
    handleDragEnd,
    handleDeleteSelected,
    handleTextDoubleClick,
    handleRectDoubleClick,
    handleEditingClick,
    handleTextEditConfirm,
    updateTextProperty,
    updateRectProperty,
    handleAddImage,
    refreshTransformer,
    // コンテキストメニュー（階層変更）
    contextMenu,
    handleObjectContextMenu,
    closeContextMenu,
    bringToFront,
    bringForward,
    sendBackward,
    sendToBack,
  };
};
